"use client"

import { useState } from "react"

interface Props {
  initial?: number
  onSubmit: (age: number) => void
}

/**
 * Age-group buckets. Stored value is the lower bound, so the
 * existing `age >= 30` gate in `lib/quiz/recommend.ts` (mamma MRI
 * eligibility) still resolves correctly without further changes.
 */
const GROUPS: Array<{ label: string; value: number }> = [
  { label: "18–29", value: 18 },
  { label: "30–39", value: 30 },
  { label: "40–49", value: 40 },
  { label: "50–59", value: 50 },
  { label: "60–69", value: 60 },
  { label: "70+", value: 70 },
]

function groupLowerBoundFor(age: number | undefined): number | null {
  if (typeof age !== "number") return null
  for (let i = GROUPS.length - 1; i >= 0; i--) {
    if (age >= GROUPS[i].value) return GROUPS[i].value
  }
  return null
}

export function AgeStep({ initial, onSubmit }: Props) {
  const [picked, setPicked] = useState<number | null>(() =>
    groupLowerBoundFor(initial)
  )
  // Only freeze the buttons while the brief auto-advance animation runs.
  // Otherwise a user who goes back from a later step couldn't pick a
  // different bucket — `picked` is still populated from the prior answer.
  const [isAdvancing, setIsAdvancing] = useState(false)

  const handleClick = (value: number) => {
    if (isAdvancing) return
    setPicked(value)
    setIsAdvancing(true)
    // Hold the highlighted state briefly so the user sees their click land
    // before the wizard transitions to the next step — matches GenderStep.
    window.setTimeout(() => onSubmit(value), 220)
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {GROUPS.map((g) => {
        const isPicked = picked === g.value
        return (
          <button
            key={g.value}
            type="button"
            onClick={() => handleClick(g.value)}
            disabled={isAdvancing && !isPicked}
            className={
              "type-title-md cursor-pointer rounded-[12px] border px-6 py-5 text-center tabular-nums transition-colors disabled:cursor-not-allowed " +
              (isPicked
                ? "border-deep-blue-900 bg-deep-blue-900 text-white"
                : "hover:border-deep-blue-900 hover:bg-deep-blue-50 border-neutral-300 bg-white text-neutral-900 disabled:opacity-40")
            }
          >
            {g.label}
          </button>
        )
      })}
    </div>
  )
}
